import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';

import { APPOINTMENT_STATUS } from '../constants';
import type { Appointment } from '../types';

type AppointmentVideoCall = {
  willBeAvailable?: boolean;
  isDisabled?: boolean;
  disabledReason?: string;
  joinVideoCall?: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

export default function useAppointmentVideoCall(appointment: Appointment): AppointmentVideoCall {
  const { enqueueSnackbar } = useSnackbar();

  if (!appointment?.videoCallUrl) return {};

  if (appointment?.status !== APPOINTMENT_STATUS.BOOKED) {
    return {
      willBeAvailable: false,
      isDisabled: true,
      disabledReason: `Appointment status is ${appointment.status}`,
    };
  }

  const appointmentStart = dayjs(appointment.startDate);
  const isAppointmentPassed = dayjs().isAfter(appointmentStart.add(1, 'hour'));
  const isVideoCallAvailable =
    dayjs().isAfter(appointmentStart.subtract(10, 'minutes')) && !isAppointmentPassed;

  const handleJoinVideoCall = (e: React.MouseEvent<HTMLButtonElement>) => {
    try {
      e.preventDefault();
      e.stopPropagation();

      if (!appointment.videoCallUrl) return;

      const videoCallUrl = new URL(appointment.videoCallUrl);
      if (!videoCallUrl.searchParams.has('view')) {
        videoCallUrl.searchParams.append('view', 'pharmacist');
      }

      const win = window.open(videoCallUrl.toString(), '_blank', 'noopener,noreferrer');
      if (win) win.opener = null;
    } catch (error) {
      enqueueSnackbar(
        `Failed to join video call: ${error instanceof Error ? error.message : error}`,
        { variant: 'error' }
      );
    }
  };

  return {
    willBeAvailable: !isAppointmentPassed && !isVideoCallAvailable,
    isDisabled: !isVideoCallAvailable,
    disabledReason: getDisabledReason(isVideoCallAvailable, isAppointmentPassed),
    joinVideoCall: isVideoCallAvailable ? handleJoinVideoCall : undefined,
  };
}

function getDisabledReason(isVideoCallAvailable: boolean, isAppointmentPassed: boolean) {
  if (isAppointmentPassed) return 'Appointment has passed';
  if (!isVideoCallAvailable)
    return 'Video call will be available 10 minutes before appointment time';
  return undefined;
}
